/**
 * TanStack Query Setup.
 *
 * Defaults sind konservativ (kein automatisches Refetch beim Window-Focus
 * – Single-User-Tool, dafür expliziter "Aktualisieren"-Button in den Listen).
 * Stale-Time 30s, Retry 1x außer bei 4xx (siehe shouldRetry).
 */

import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (error instanceof ApiError) {
    // 4xx: in der Regel Client-Fehler, kein Retry sinnvoll.
    if (error.status >= 400 && error.status < 500) return false;
  }
  return true;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: shouldRetry,
    },
    mutations: {
      retry: false,
    },
  },
});

export const queryKeys = {
  customers: {
    all: ['customers'] as const,
    list: (filters: Record<string, unknown>) => ['customers', 'list', filters] as const,
    detail: (id: string) => ['customers', 'detail', id] as const,
    locations: (id: string) => ['customers', 'detail', id, 'locations'] as const,
    stats: (id: string) => ['customers', 'detail', id, 'stats'] as const,
    hnfLink: (id: string) => ['customers', 'detail', id, 'hnf-link'] as const,
    notificationRecipients: (id: string) =>
      ['customers', 'detail', id, 'notification-recipients'] as const,
    effectiveRecipients: (id: string) =>
      ['customers', 'detail', id, 'notification-recipients', 'effective'] as const,
    contacts: (id: string) => ['customers', 'detail', id, 'contacts'] as const,
  },
  systems: {
    all: ['systems'] as const,
    list: (filters: Record<string, unknown>) => ['systems', 'list', filters] as const,
    // Rollen-Vorschläge (GET /systems/roles, Spec S8-06 Kontrakt 2).
    roles: ['systems', 'roles'] as const,
    // Facets Rolle/OS/VPN/Domain/Node (GET /systems/facets). Der Kunde geht in
    // den Schlüssel ein: vpn_refs, domains und proxmox_nodes sortieren seine
    // Werte nach vorn.
    facets: (customerId?: string) => ['systems', 'facets', customerId ?? null] as const,
    detail: (id: string) => ['systems', 'detail', id] as const,
    services: (id: string) => ['systems', 'detail', id, 'services'] as const,
    connectionProfiles: (id: string) =>
      ['systems', 'detail', id, 'connection-profiles'] as const,
    zabbixPackages: (id: string) =>
      ['systems', 'detail', id, 'zabbix-packages'] as const,
  },
  contracts: {
    all: ['contracts'] as const,
    list: (filters: Record<string, unknown>) => ['contracts', 'list', filters] as const,
    detail: (id: string) => ['contracts', 'detail', id] as const,
    byCustomer: (customerId: string) => ['contracts', 'by-customer', customerId] as const,
    unmapped: ['contracts', 'unmapped'] as const,
    ignoredClients: ['contracts', 'ignored-clients'] as const,
    // Vertragsabgleich (Fall Fortis Unum): je Kunde die Positionen mit ihren
    // Systemen, plus die kundenübergreifende Liste offener Änderungen für
    // Seite und Sidebar-Badge.
    reconciliation: (customerId: string) =>
      ['contracts', 'reconciliation', customerId] as const,
    changes: ['contracts', 'changes'] as const,
  },
  maintenancePlans: {
    all: ['maintenance-plans'] as const,
    byCustomer: (customerId: string) => ['maintenance-plans', 'by-customer', customerId] as const,
    detail: (id: string) => ['maintenance-plans', 'detail', id] as const,
  },
  serviceTypes: {
    all: ['service-types'] as const,
    unknowns: ['service-types', 'unknowns'] as const,
  },
  invoiceNinja: {
    syncStatus: ['invoice-ninja', 'sync-status'] as const,
  },
  hnf: {
    status: ['hnf', 'status'] as const,
    customers: (search: string | undefined) => ['hnf', 'customers', search ?? ''] as const,
  },
  sessions: {
    all: ['sessions'] as const,
    current: ['sessions', 'current'] as const,
    activeHosts: (sessionId: string) => ['sessions', sessionId, 'active-hosts'] as const,
  },
  sshConfig: {
    state: ['ssh-config', 'state'] as const,
  },
  mail: {
    config: ['mail', 'config'] as const,
    templates: ['mail', 'templates'] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: (filters: Record<string, unknown>) => ['notifications', 'list', filters] as const,
    draftCount: ['notifications', 'draft-count'] as const,
  },
} as const;
